import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { generateStudentDocumentPdf, getDocumentFieldForm, previewGeneratedDocument } from '../../api/students';
import {
  DocumentField,
  DocumentFieldForm,
  DocumentFieldSource,
  EditableDocumentTemplateKey,
  SIGNATURE_IMAGE_TEMPLATE_KEYS,
  StudentDocument,
} from '../../types/student';
import { StudentSignatureAdapter, StudentSignatureSection } from './StudentSignatureSection';

type StepId = 'details' | 'preview' | 'signature' | 'generate';
const STEP_LABELS: Record<StepId, string> = {
  details: 'Document details',
  preview: 'Preview',
  signature: 'Signature',
  generate: 'Generate',
};
/** The Signature step only for templates that place the saved signature
 * image (Phase 109: the Health Declaration prints typed names instead). */
const stepsFor = (templateKey: EditableDocumentTemplateKey): StepId[] =>
  SIGNATURE_IMAGE_TEMPLATE_KEYS.includes(templateKey)
    ? ['details', 'preview', 'signature', 'generate']
    : ['details', 'preview', 'generate'];

/** Health Declaration: a condition's "if no, please state" field sits
 * beside its YES/NO answer rather than on a line of its own. */
const pairedStatementKey = (key: string) => (key.startsWith('answer_') ? `statement_${key.slice('answer_'.length)}` : null);

const SOURCE_LABEL: Record<DocumentFieldSource, string> = {
  STUDENT: 'From the student record',
  MASTER_DATA: 'From master data',
  TEMPLATE_DEFAULT: 'Saved template default',
  BUILT_IN: 'Standard template text',
  NONE: 'Not in the student record — fill in if needed',
};

/** Error bodies of a `responseType: 'blob'` request arrive as a Blob, not
 * parsed JSON — read the API's own message out of it when there is one. */
const describeError = async (err: unknown): Promise<string> => {
  if (isAxiosError(err)) {
    const body = err.response?.data;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        if (parsed?.message) return parsed.message;
      } catch {
        // not JSON
      }
    } else if (body?.message) {
      return body.message;
    }
  }
  return 'Something went wrong. Please try again.';
};

const fieldHelperText = (field: DocumentField, universityName: string): string => {
  const parts: string[] = [SOURCE_LABEL[field.source]];
  if (field.source === 'TEMPLATE_DEFAULT') {
    parts[0] = `Saved default for ${universityName || 'this university'}`;
    if (field.baseValue && field.baseValue !== field.defaultValue) {
      parts.push(`${SOURCE_LABEL[field.baseSource ?? 'NONE']}: "${field.baseValue}"`);
    }
  }
  if (field.helperText) parts.push(field.helperText);
  return parts.join(' · ');
};

/** Groups fields in definition order, template text first. */
const groupFields = (fields: DocumentField[]) => {
  const groups = new Map<string, DocumentField[]>();
  for (const field of fields) {
    groups.set(field.group, [...(groups.get(field.group) ?? []), field]);
  }
  return [...groups.entries()];
};

const PdfPreview = ({ blobUrl, loading, error, title }: { blobUrl: string | null; loading: boolean; error: string | null; title: string }) => (
  <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
    {loading && (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )}
    {!loading && error && <Alert severity="error">{error}</Alert>}
    {!loading && !error && blobUrl && (
      <Box component="iframe" src={blobUrl} title={title} sx={{ flex: 1, border: 1, borderColor: 'divider' }} />
    )}
  </Box>
);

/** Editable document fields (Phase 91) — the generate flow for the
 * University Application Form and the Admission Consultancy Services
 * Agreement: edit pre-filled fields → preview → apply the student's
 * signature (the existing StudentSignatureSection, unchanged) → generate
 * and download. Template text can optionally be saved as the university's
 * default; every other edit applies to this one document only. */
export const GenerateDocumentDialog = ({
  open,
  studentId,
  templateKey,
  title,
  signatureAdapter,
  onClose,
  onGenerated,
}: {
  open: boolean;
  studentId: number;
  templateKey: EditableDocumentTemplateKey;
  title: string;
  signatureAdapter: StudentSignatureAdapter;
  onClose: () => void;
  onGenerated: (document: StudentDocument) => void;
}) => {
  const [step, setStep] = useState(0);
  const steps = stepsFor(templateKey);
  const stepId = steps[step];
  const usesSignatureImage = steps.includes('signature');
  const [values, setValues] = useState<Record<string, string>>({});
  const [saveTemplateDefaults, setSaveTemplateDefaults] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const formQuery = useQuery({
    queryKey: ['document-field-form', studentId, templateKey],
    queryFn: () => getDocumentFieldForm(studentId, templateKey),
    enabled: open,
    // Always start from fresh pre-fills (student data or a template
    // default may have changed since the dialog was last opened).
    gcTime: 0,
  });
  const form: DocumentFieldForm | undefined = formQuery.data;

  // Shares StudentSignatureSection's own query, so a signature saved in
  // step 3 is reflected here (and in the final preview) straight away.
  const signatureQuery = useQuery({
    queryKey: signatureAdapter.queryKey,
    queryFn: signatureAdapter.getSignature,
    enabled: open,
  });
  const signatureVersion = signatureQuery.data?.updatedAt ?? null;

  useEffect(() => {
    if (form) {
      setValues(Object.fromEntries(form.fields.map((field) => [field.key, field.defaultValue])));
    }
  }, [form]);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setSaveTemplateDefaults(false);
      setGenerateError(null);
      setPreviewError(null);
      setPreviewUrl(null);
    }
  }, [open]);

  // The preview is re-rendered each time the preview or final step is
  // shown, so it always reflects the current edits and signature.
  useEffect(() => {
    if (!open || (stepId !== 'preview' && stepId !== 'generate')) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    previewGeneratedDocument(studentId, templateKey, values)
      .then((blob) => {
        if (!cancelled) setPreviewUrl(URL.createObjectURL(blob));
      })
      .catch(async (err) => {
        if (!cancelled) setPreviewError(await describeError(err));
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, signatureVersion]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const editedKeys = useMemo(
    () => (form ? form.fields.filter((field) => (values[field.key] ?? '') !== field.defaultValue).map((field) => field.key) : []),
    [form, values],
  );

  const overLimit = form?.fields.some((field) => (values[field.key] ?? '').length > field.maxLength) ?? false;

  const generateMutation = useMutation({
    mutationFn: () => generateStudentDocumentPdf(studentId, templateKey, { fieldValues: values, saveTemplateDefaults }),
    onSuccess: (document) => {
      setGenerateError(null);
      onGenerated(document);
    },
    onError: async (err) => setGenerateError(await describeError(err)),
  });

  const canSaveDefaults = !!form?.canSaveTemplateDefaults && form.universityId != null;
  const hasTemplateFields = form?.fields.some((field) => field.scope === 'TEMPLATE') ?? false;

  const renderField = (field: DocumentField) => {
    const value = values[field.key] ?? '';
    const edited = value !== field.defaultValue;
    return (
      <Stack key={field.key} direction="row" spacing={1} alignItems="flex-start">
        <TextField
          fullWidth
          size="small"
          label={field.label}
          value={value}
          select={field.input === 'select'}
          multiline={field.input === 'textarea'}
          // An empty select value is a real choice ("Leave blank"), so its
          // option label must show rather than the floating field label.
          slotProps={field.input === 'select' ? { select: { displayEmpty: true }, inputLabel: { shrink: true } } : undefined}
          minRows={field.input === 'textarea' ? 2 : undefined}
          onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
          error={value.length > field.maxLength}
          helperText={
            value.length > field.maxLength
              ? `At most ${field.maxLength} characters`
              : edited
                ? `Edited for this document · was "${field.defaultValue || '(blank)'}"`
                : fieldHelperText(field, form?.universityName ?? '')
          }
        >
          {field.options?.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Reset to pre-filled value">
          <span>
            <IconButton
              size="small"
              aria-label={`Reset ${field.label}`}
              disabled={!edited}
              onClick={() => setValues((prev) => ({ ...prev, [field.key]: field.defaultValue }))}
              sx={{ mt: 0.5 }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  };

  const fieldGroups = form ? groupFields(form.fields) : [];
  const firstDocumentGroup = fieldGroups.find(([, fields]) => fields[0].scope === 'DOCUMENT')?.[0];

  const detailsStep = form && (
    <Stack spacing={3}>
      {fieldGroups.map(([group, fields]) => (
        <Box key={group}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {group}
          </Typography>
          {fields[0].scope === 'TEMPLATE' ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Template text that changes periodically (e.g. each intake).
              {form.templateDefaults
                ? ` Pre-filled from the default saved for ${form.universityName} on ${new Date(form.templateDefaults.updatedAt).toLocaleDateString()} by ${form.templateDefaults.updatedBy.firstName} ${form.templateDefaults.updatedBy.lastName}.`
                : ' No default has been saved for this university yet.'}
            </Alert>
          ) : (
            group === firstDocumentGroup && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Pre-filled from the student&apos;s application. Changes here apply to this document only — the
                student&apos;s record is not updated.
              </Alert>
            )
          )}
          <Stack spacing={1.5}>
            {fields
              .filter((field) => !(field.key.startsWith('statement_') && fields.some((f) => pairedStatementKey(f.key) === field.key)))
              .map((field) => {
                const pairedKey = pairedStatementKey(field.key);
                const paired = pairedKey ? fields.find((f) => f.key === pairedKey) : undefined;
                if (!paired) return renderField(field);
                return (
                  <Box key={field.key} sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '2fr 3fr' } }}>
                    {renderField(field)}
                    {renderField(paired)}
                  </Box>
                );
              })}
          </Stack>
        </Box>
      ))}
      {hasTemplateFields && canSaveDefaults && (
        <FormControlLabel
          control={<Checkbox checked={saveTemplateDefaults} onChange={(e) => setSaveTemplateDefaults(e.target.checked)} />}
          label={`Also save the template text as the default for ${form.universityName} (used to pre-fill future documents; documents already generated are not changed)`}
        />
      )}
    </Stack>
  );

  const signatureStep = (
    <Stack spacing={2}>
      <Alert severity={signatureQuery.data ? 'success' : 'warning'}>
        {signatureQuery.data
          ? 'The saved signature below will be placed on the document\'s signature line. You can replace it here.'
          : templateKey === 'ADMISSION_AGREEMENT'
            ? 'No signature saved yet — the agreement will show "Signature Required". Draw or upload one below.'
            : 'No signature saved yet — the signature line will be left blank. Draw or upload one below.'}
      </Alert>
      <StudentSignatureSection adapter={signatureAdapter} />
    </Stack>
  );

  const generateStep = (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={editedKeys.length ? `${editedKeys.length} field(s) edited` : 'No fields edited'} />
        {usesSignatureImage && (
          <Chip
            size="small"
            color={signatureQuery.data ? 'success' : 'default'}
            label={signatureQuery.data ? 'Signature applied' : 'No signature'}
          />
        )}
        {saveTemplateDefaults && <Chip size="small" color="primary" label={`Template default will be saved for ${form?.universityName}`} />}
      </Stack>
      {generateError && <Alert severity="error">{generateError}</Alert>}
      <PdfPreview blobUrl={previewUrl} loading={previewLoading} error={previewError} title={`${title} preview`} />
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate {title}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((id) => (
            <Step key={id}>
              <StepLabel>{STEP_LABELS[id]}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {formQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {formQuery.isError && <Alert severity="error">Could not load the document details. Please try again.</Alert>}
        {form && stepId === 'details' && detailsStep}
        {form && stepId === 'preview' && <PdfPreview blobUrl={previewUrl} loading={previewLoading} error={previewError} title={`${title} preview`} />}
        {form && stepId === 'signature' && signatureStep}
        {form && stepId === 'generate' && generateStep}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={generateMutation.isPending}>
            Back
          </Button>
        )}
        {step < steps.length - 1 ? (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={!form || overLimit}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => generateMutation.mutate()}
            disabled={!form || overLimit || generateMutation.isPending}
          >
            {generateMutation.isPending ? 'Generating…' : 'Generate & download'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
